import { useMemo } from 'react';
import { useAssetsIcons } from '@/shared/api/queries/useAssetsIconsDictionary';
import { sum } from '@/shared/lib';
import {
  useAccountsInfo,
  type AccountMarginRest,
  type AccountRest,
} from '@/entities/account';

type AccountRow = {
  accNum: string;
  tradingAccKey: string;
  assetCode: string;
  iconUrl?: string;
  leverage?: number;
  balance: number;
};

const getAccountBalance = (account: AccountRest | AccountMarginRest) => {
  if ('assets' in account) {
    return sum(
      account.assets.map(
        asset => (asset.baseAvailable ?? 0) + (asset.baseReserved ?? 0)
      )
    );
  }

  return account.baseEquity ?? 0;
};

const getAssetCode = (account: AccountRest | AccountMarginRest) => {
  if ('balanceAsset' in account && account.balanceAsset) {
    return account.balanceAsset.code;
  }

  return 'assets' in account ? (account.assets[0]?.code ?? '') : '';
};

const useAccountRows = (
  accounts: Array<AccountRest | AccountMarginRest>
): Array<AccountRow> => {
  const { assetsIconsData } = useAssetsIcons();
  const { accountsInfo } = useAccountsInfo();

  return useMemo(() => {
    const leverageByKey = new Map(
      (accountsInfo?.accounts ?? [])
        .filter(account => account.tradingAccKey != null)
        .map(account => [
          String(account.tradingAccKey),
          account.leverage?.value,
        ])
    );

    return accounts.map(account => {
      const assetCode = getAssetCode(account);

      return {
        accNum: account.accNum,
        tradingAccKey: String(account.tradingAccKey ?? ''),
        assetCode,
        iconUrl: assetsIconsData?.value?.[assetCode],
        leverage: leverageByKey.get(String(account.tradingAccKey ?? '')),
        balance: getAccountBalance(account),
      };
    });
  }, [accounts, accountsInfo, assetsIconsData]);
};

export { useAccountRows, type AccountRow };
